import { ProductImageUpload } from './ProductImageUpload';

export interface ProductFormData {
  id?: string;
  name: string;
  description?: string;
  category?: string;
  originalPrice: number;
  stock: number;
  imageFile?: File | null;
  imageUrl?: string;
  discountRate?: number;
  discountedPrice?: number;
}

interface ProductFormCardProps {
  product: ProductFormData;
  onChange: (product: ProductFormData) => void;
  onDelete?: () => void;
  selected?: boolean;
  onSelect?: (selected: boolean) => void;
  disabled?: boolean;
  showDeleteButton?: boolean;
  showCheckbox?: boolean;
  showValidation?: boolean;
  fallbackImageUrl?: string;
  invalidFields?: {
    price?: boolean;
    stock?: boolean;
    name?: boolean;
  };
  showDiscountControls?: boolean;
  discountType?: 'deadline' | 'reservation';
}

export function ProductFormCard({
  product,
  onChange,
  onDelete,
  selected = false,
  onSelect,
  disabled = false,
  showDeleteButton = false,
  showCheckbox = false,
  showValidation = false,
  fallbackImageUrl,
  invalidFields,
  showDiscountControls = false,
  discountType
}: ProductFormCardProps) {
  const getDefaultDiscountRate = () => {
    if (discountType === 'deadline') return 40;
    if (discountType === 'reservation') return 5;
    return 0;
  };

  const getDiscountLimits = () => {
    if (discountType === 'deadline') {
      return { min: 40, max: 80, step: 5 };
    }
    return { min: 0, max: 10, step: 1 };
  };

  const limits = getDiscountLimits();
  const effectiveRate = product.discountRate ?? getDefaultDiscountRate();
  const effectiveDiscountedPrice =
    product.discountedPrice ?? Math.floor(product.originalPrice * (100 - effectiveRate) / 100);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('ko-KR').format(value);
  };

  const handleFieldChange = (field: keyof ProductFormData, value: any) => {
    if (field === 'imageFile' && value === null) {
      // 이미지 삭제 시 imageUrl도 함께 제거
      onChange({
        ...product,
        imageFile: null,
        imageUrl: undefined
      });
    } else {
      onChange({
        ...product,
        [field]: value
      });
    }
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/,/g, '');
    const numValue = parseInt(value) || 0;
    if (showDiscountControls && discountType) {
      const rate = product.discountRate ?? getDefaultDiscountRate();
      const discountedPrice = Math.floor(numValue * (100 - rate) / 100);
      onChange({
        ...product,
        originalPrice: numValue,
        discountRate: rate,
        discountedPrice
      });
    } else {
      handleFieldChange('originalPrice', numValue);
    }
  };

  const handleStockChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 0;
    handleFieldChange('stock', value);
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    handleFieldChange('description', e.target.value);
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFieldChange('category', e.target.value);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSelect?.(e.target.checked);
  };

  const handleDiscountRateChange = (rate: number) => {
    const clamped = Math.min(limits.max, Math.max(limits.min, rate));
    const discountedPrice = Math.floor(product.originalPrice * (100 - clamped) / 100);
    onChange({
      ...product,
      discountRate: clamped,
      discountedPrice
    });
  };

  const isDisabled = showCheckbox ? disabled || !selected : disabled;
  const deleteDisabled = disabled;
  const nameInvalid = showValidation && invalidFields?.name;
  const priceInvalid = showValidation && invalidFields?.price;
  const stockInvalid = showValidation && invalidFields?.stock;

  return (
    <div className={`relative bg-white border rounded-lg p-4 ${showDeleteButton ? 'pr-12' : ''} transition-colors ${
      selected ? 'border-green-500' : 'border-gray-200'
    }`}>
      {showDeleteButton && (
        <button
          onClick={onDelete}
          disabled={deleteDisabled}
          className="absolute top-3 right-3 p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:text-gray-300 disabled:hover:bg-transparent"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      )}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* 체크박스 */}
        {showCheckbox && (
          <div className="flex items-start pt-1">
            <input
              type="checkbox"
              checked={selected}
              onChange={handleCheckboxChange}
              disabled={disabled}
              className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
            />
          </div>
        )}

        {/* 이미지 업로드 */}
        <ProductImageUpload
          imageUrl={product.imageFile ? URL.createObjectURL(product.imageFile) : product.imageUrl}
          fallbackImageUrl={fallbackImageUrl}
          onImageChange={(file) => handleFieldChange('imageFile', file)}
          disabled={isDisabled}
          containerClassName="w-full h-36 sm:w-24 sm:h-24 sm:shrink-0"
        />

        {/* 상품 정보 입력 */}
        <div className="flex-1 space-y-3 w-full">
          {/* 상품명 */}
          <input
            type="text"
            value={product.name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            placeholder="상품명을 입력하세요"
            disabled={isDisabled}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none disabled:bg-gray-100 ${
              nameInvalid ? 'border-red-400 focus:ring-red-500' : 'border-gray-300 focus:ring-2 focus:ring-green-500'
            }`}
          />

          {/* 카테고리 */}
          <div className="mt-3">
            <label className="block text-sm text-gray-600 mb-1">
              카테고리 <span className="text-gray-400">(선택)</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={product.category || ''}
                onChange={handleCategoryChange}
                placeholder="카테고리를 입력하세요 (선택, 최대 20자)"
                maxLength={20}
                disabled={isDisabled}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
              />
              <span className={`absolute bottom-2 right-2 text-xs ${
                (product.category?.length || 0) >= 20 ? 'text-red-500' : 'text-gray-400'
              }`}>
                {product.category?.length || 0}/20
              </span>
            </div>
          </div>

          {/* 가격과 재고 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">정상가</label>
              <div className="relative">
                <input
                  type="text"
                  value={formatCurrency(product.originalPrice)}
                  onChange={handlePriceChange}
                  placeholder="0"
                  disabled={isDisabled}
                  className={`w-full px-3 py-2 pr-8 border rounded-lg focus:outline-none disabled:bg-gray-100 ${
                    priceInvalid ? 'border-red-400 focus:ring-red-500' : 'border-gray-300 focus:ring-2 focus:ring-green-500'
                  }`}
                />
                <span className="absolute right-3 top-2.5 text-gray-500">원</span>
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">기본재고</label>
              <div className="relative">
                <input
                  type="number"
                  value={product.stock}
                  onChange={handleStockChange}
                  placeholder="20"
                  min="0"
                  disabled={isDisabled}
                  className={`w-full px-3 py-2 pr-8 border rounded-lg focus:outline-none disabled:bg-gray-100 ${
                    stockInvalid ? 'border-red-400 focus:ring-red-500' : 'border-gray-300 focus:ring-2 focus:ring-green-500'
                  }`}
                />
                <span className="absolute right-3 top-2.5 text-gray-500">개</span>
              </div>
            </div>
          </div>

          {showDiscountControls && discountType && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">할인율</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleDiscountRateChange(effectiveRate - limits.step)}
                    disabled={isDisabled || effectiveRate <= limits.min}
                    className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-md bg-white disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    min={limits.min}
                    max={limits.max}
                    step={limits.step}
                    value={effectiveRate}
                    onChange={(e) => handleDiscountRateChange(Number(e.target.value))}
                    disabled={isDisabled}
                    className="w-16 px-2 py-1.5 text-center border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:text-gray-500"
                  />
                  <span className="text-sm text-gray-600">%</span>
                  <button
                    type="button"
                    onClick={() => handleDiscountRateChange(effectiveRate + limits.step)}
                    disabled={isDisabled || effectiveRate >= limits.max}
                    className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-md bg-white disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50"
                  >
                    +
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {discountType === 'deadline'
                    ? `마감할인: ${limits.min}% 이상`
                    : `예약할인: ${limits.min}% ~ ${limits.max}%`}
                </p>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">할인가 (자동)</label>
                <div className="relative">
                  <input
                    type="text"
                    value={formatCurrency(effectiveDiscountedPrice)}
                    readOnly
                    className="w-full px-3 py-2 pr-8 border rounded-lg bg-gray-50 text-gray-700"
                  />
                  <span className="absolute right-3 top-2.5 text-gray-500">원</span>
                </div>
              </div>
            </div>
          )}

          {/* 상품 설명 */}
          <div className="mt-3">
            <label className="block text-sm text-gray-600 mb-1">
              간단한 설명 <span className="text-gray-400">(선택)</span>
            </label>
            <div className="relative">
              <textarea
                value={product.description || ''}
                onChange={handleDescriptionChange}
                placeholder="상품을 간단히 설명해주세요 (선택, 최대 100자)"
                maxLength={100}
                disabled={isDisabled}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100 resize-none"
              />
              <span className={`absolute bottom-2 right-2 text-xs ${
                (product.description?.length || 0) >= 100 ? 'text-red-500' : 'text-gray-400'
              }`}>
                {product.description?.length || 0}/100
              </span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
